import { useState } from "react";
import { useGetList } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Store, Plus, DollarSign } from "lucide-react";
import { VendorStatusBadge } from "./VendorStatusBadge";
import { VendorDetail } from "./VendorDetail";
import { CreateVendorDialog } from "./CreateVendorDialog";

interface Vendor {
  id: string;
  name: string;
  contact_name: string;
  payment_terms: string;
  status: string;
  total_ordered: number;
  total_paid: number;
  balance_owed: number;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

const TERMS_SHORT: Record<string, string> = {
  cod: "COD", net_15: "N15", net_30: "N30", net_45: "N45", net_60: "N60",
};

export function VendorList() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isPending, total } = useGetList<Vendor>("vendors", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  if (selectedId) {
    return <VendorDetail vendorId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div style={{ padding: 24 }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Store className="w-5 h-5" />
          Vendors
          {total !== undefined && total > 0 && (
            <span className="text-sm font-normal text-muted-foreground">({total})</span>
          )}
        </h2>
        <button onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
          <Plus className="w-3.5 h-3.5" /> Add Vendor
        </button>
      </div>

      {isPending && <p className="text-sm text-muted-foreground py-4">Loading…</p>}

      {!isPending && (!data || data.length === 0) && (
        <Card>
          <CardContent className="p-8 text-center">
            <Store className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground mb-3">No vendors yet.</p>
            <button onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 mx-auto px-3 py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700">
              <Plus className="w-3 h-3" /> Add First Vendor
            </button>
          </CardContent>
        </Card>
      )}

      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((v) => (
            <Card key={v.id} className="cursor-pointer hover:border-blue-200 transition-colors"
              onClick={() => setSelectedId(v.id)}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <VendorStatusBadge status={v.status} />
                      <span className="text-xs text-muted-foreground bg-gray-100 px-1 py-0.5 rounded">
                        {TERMS_SHORT[v.payment_terms] || v.payment_terms}
                      </span>
                    </div>
                    <p className="text-sm font-medium truncate">{v.name}</p>
                    {v.contact_name && (
                      <p className="text-xs text-muted-foreground">{v.contact_name}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold tabular-nums" style={{
                      color: Number(v.balance_owed) > 0 ? "#d97706" : "#16a34a",
                    }}>
                      {fmt(Number(v.balance_owed))}
                    </p>
                    <p className="text-xs text-muted-foreground">owed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateVendorDialog open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(id) => setSelectedId(id)} />
    </div>
  );
}

VendorList.path = "/vendors";
